import React from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Box,
  Paper,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Stack,
  Rating,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  Button,
  Link,
} from '@mui/material';
import { MapPin, Phone, Mail, Building2, Users, Home, Briefcase, ExternalLink } from 'lucide-react';
import { mockAgencies, AgencyType, mockAgents, AgentType, mockProperties, PropertyType } from '../utils/mockData';
import PropertyCard from '../components/property/PropertyCard'; // Default import

const AgencyDetailPage: React.FC = () => {
  const { agencyId } = useParams<{ agencyId: string }>();
  const agency = mockAgencies.find(a => a.id === parseInt(agencyId || ''));

  if (!agency) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h4" component="h1">Agency not found</Typography>
        </Paper>
      </Container>
    );
  }

  const agencyAgents = mockAgents.filter(agent => agent.agencyId === agency.id);
  const agentIdsForAgency = agencyAgents.map(agent => agent.id);
  const agencyProperties = mockProperties.filter(property => property.agentId && agentIdsForAgency.includes(property.agentId));

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Section 1: Main Agency Info */}
      <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box
              component="img"
              src={agency.logoUrl || 'https://via.placeholder.com/300x200?text=Agency+Logo'}
              alt={`${agency.name} Logo`}
              sx={{
                width: '100%',
                maxHeight: '250px',
                objectFit: 'contain',
                borderRadius: 1,
                mb: { xs: 2, md: 0 }
              }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Typography variant="h3" component="h1" gutterBottom fontWeight={700}>
              {agency.name}
            </Typography>
            <Rating value={agency.rating} precision={0.1} readOnly sx={{ mb: 2 }} />
            <Typography variant="body1" paragraph color="text.secondary" sx={{ mb: 2 }}>
              {agency.description}
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom sx={{fontSize: '1.1rem', fontWeight: 'medium'}}>Specialties:</Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {agency.specialties.map((specialty) => (
                  <Chip key={specialty} label={specialty} color="primary" variant="outlined" />
                ))}
              </Stack>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom>Contact Information</Typography>
            <List dense>
              <ListItem disablePadding>
                <ListItemIcon sx={{minWidth: '36px'}}><Phone size={20} /></ListItemIcon>
                <ListItemText primary={agency.contactPhone} />
              </ListItem>
              <ListItem disablePadding>
                <ListItemIcon sx={{minWidth: '36px'}}><Mail size={20} /></ListItemIcon>
                <ListItemText primary={<Link href={`mailto:${agency.contactEmail}`} underline="hover">{agency.contactEmail}</Link>} />
              </ListItem>
              {agency.website && (
                <ListItem disablePadding>
                  <ListItemIcon sx={{minWidth: '36px'}}><ExternalLink size={20} /></ListItemIcon>
                  <ListItemText primary={<Link href={agency.website} target="_blank" rel="noopener noreferrer" underline="hover">{agency.website}</Link>} />
                </ListItem>
              )}
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom>Office Locations</Typography>
            <List dense>
              {agency.offices.map((office, index) => (
                <ListItem key={index} disablePadding sx={{ mb: 1 }}>
                   <ListItemIcon sx={{minWidth: '36px'}}><Building2 size={20}/></ListItemIcon>
                  <ListItemText 
                    primary={office.city} 
                    secondary={`${office.address} • ${office.phone}`} 
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      </Paper>

      {/* Section 2: Agents of the Agency */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2" gutterBottom fontWeight={600} sx={{ display: 'flex', alignItems: 'center' }}>
          <Users size={28} style={{ marginRight: 12 }} /> Meet the Team at {agency.name}
        </Typography>
        {agencyAgents.length > 0 ? (
          <Grid container spacing={3}>
            {agencyAgents.map((agent) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={agent.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', transition: '0.2s', '&:hover': {transform: 'scale(1.03)'} }}>
                  <CardMedia
                    component="img"
                    height="220"
                    image={agent.image || 'https://via.placeholder.com/200x220?text=Agent'}
                    alt={agent.name}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                    <Typography variant="h6" component="div">{agent.name}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{agent.title}</Typography>
                    <Button 
                      component={RouterLink} 
                      to={`/agents/${agent.id}`} 
                      variant="outlined" 
                      size="small"
                      fullWidth
                    >
                      View Profile
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1">No agents currently listed for this agency.</Typography>
          </Paper>
        )}
      </Box>

      {/* Section 3: Properties of the Agency */}
      <Box>
        <Typography variant="h4" component="h2" gutterBottom fontWeight={600} sx={{ display: 'flex', alignItems: 'center' }}>
          <Home size={28} style={{ marginRight: 12 }} /> Properties from {agency.name}
        </Typography>
        {agencyProperties.length > 0 ? (
          <Grid container spacing={3}>
            {agencyProperties.map((property) => (
              <Grid item xs={12} sm={6} md={4} key={property.id}>
                <PropertyCard property={property} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1">No properties currently listed by this agency.</Typography>
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default AgencyDetailPage;
